import { Fragment, useEffect, useMemo, useState } from "react";
import { IDropdown } from "@/constants/default";
import { useGetBatchFinancialKpisDataQuery } from "@/services/kpi.service";
import DropdownCheckbox from "@/components/Dropdown/DropdownCheckboxKpi";

const DEFAULT_EXCLUDED_COMPARISON_UNITS = new Set(["SCKYROOM", "BANK"]);

const sortComparisonUnits = (units: IDropdown[]) =>
    [...units].sort((a, b) => {
        const aIsSpecial = DEFAULT_EXCLUDED_COMPARISON_UNITS.has(String(a.label).trim().toUpperCase());
        const bIsSpecial = DEFAULT_EXCLUDED_COMPARISON_UNITS.has(String(b.label).trim().toUpperCase());

        if (aIsSpecial === bIsSpecial) return 0;
        return aIsSpecial ? 1 : -1;
    });

const formatValue = (
    value: number | null | undefined,
    format: "percent" | "currency" | "days" | "number"
) => {
    if (value == null || Number.isNaN(value)) return "N/A";

    if (format === "currency") {
        return value.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            currencyDisplay: "symbol",
            maximumFractionDigits: 0,
        });
    }
    if (format === "percent") {
        return `${value.toFixed(2)}%`;
    }
    if (format === "days") {
        return `${Math.round(value)} days`;
    }
    return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const formatMonthlyValue = (value: number | null) => {
    if (value == null || Number.isNaN(value)) return "N/A";
    return `${formatValue(value, "currency")}/mth`;
};

type HistoricalColumn = {
    key: string;
    label: string;
    range: string;
};

type MonthlyChartColumn = {
    key: string;
    label: string;
    start: string;
    end: string;
};

const getLatestFiveCalendarYearColumnsCompletedMonth = (): HistoricalColumn[] => {
    const currentYear = new Date().getFullYear();

    return [
        { key: `year_${currentYear}`, label: String(currentYear), range: "current_year_through_last_completed_month" },
        ...Array.from({ length: 4 }, (_, index) => {
            const year = currentYear - 1 - index;
            return {
                key: `year_${year}`,
                label: String(year),
                range: `year_${year}`,
            };
        }),
    ];
};

const getLast12CompletedMonthColumns = (): MonthlyChartColumn[] => {
    const today = new Date();
    const columns: MonthlyChartColumn[] = [];

    // Start from last completed month (previous month from today)
    let year = today.getFullYear();
    let month = today.getMonth(); // Current month (0-indexed)

    for (let i = 0; i < 12; i++) {
        // Go back one month
        month--;
        if (month < 0) {
            month = 11;
            year--;
        }

        const monthStr = (month + 1).toString().padStart(2, "0");
        const lastDay = new Date(year, month + 1, 0).getDate();

        const key = `${year}-${monthStr}`;
        const label = new Date(year, month, 1).toLocaleDateString("en-US", {
            month: "short",
            year: "2-digit",
        });

        const start = `${year}-${monthStr}-01`;
        const end = `${year}-${monthStr}-${lastDay.toString().padStart(2, "0")}`;

        columns.push({ key, label, start, end });
    }

    return columns; // Already in reverse chronological order (recent on left)
};

interface CashflowNestedDrilldownProps {
    title: string;
    businessUnits: IDropdown[];
    inactiveUnitIds?: Set<string>;
}

export function CashflowNestedDrilldown({
    title,
    businessUnits,
    inactiveUnitIds,
}: CashflowNestedDrilldownProps) {
    const STORAGE_KEY = "kpi_cashflow_selected_units";
    const [selectedUnits, setSelectedUnits] = useState<IDropdown[]>([]);
    const [expandedUnitIds, setExpandedUnitIds] = useState<Set<string>>(new Set());
    const yearlyColumns = useMemo(() => getLatestFiveCalendarYearColumnsCompletedMonth(), []);
    const monthColumns = useMemo(() => getLast12CompletedMonthColumns(), []);
    const comparisonUnits = useMemo(() => sortComparisonUnits(businessUnits), [businessUnits]);
    const defaultSelectedUnits = useMemo(
        () => comparisonUnits.filter((unit) => !inactiveUnitIds?.has(String(unit.value))),
        [comparisonUnits, inactiveUnitIds]
    );

    useEffect(() => {
        if (!comparisonUnits.length) {
            setSelectedUnits([]);
            return;
        }

        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const storedIds = JSON.parse(stored) as string[];
                const restoredUnits = comparisonUnits.filter((unit) =>
                    storedIds.includes(String(unit.value)) &&
                    !inactiveUnitIds?.has(String(unit.value))
                );
                if (restoredUnits.length > 0) {
                    setSelectedUnits(restoredUnits);
                    return;
                }
            }
        } catch (error) {
            console.error("Failed to load saved unit selection:", error);
        }

        setSelectedUnits(defaultSelectedUnits);
    }, [comparisonUnits, defaultSelectedUnits]);

    useEffect(() => {
        if (selectedUnits.length > 0) {
            const unitIds = selectedUnits.map((u) => String(u.value));
            localStorage.setItem(STORAGE_KEY, JSON.stringify(unitIds));
        }
    }, [selectedUnits]);

    const allUnitIds = useMemo(
        () => Array.from(new Set(comparisonUnits.map((unit) => String(unit.value)))).sort((a, b) => a.localeCompare(b)),
        [comparisonUnits]
    );

    // Fetch data for last 12 months
    const chartBatchArgs = useMemo(
        () => ({
            level: "L2",
            metrics: ["cashflow"],
            requests: allUnitIds.flatMap((unitId) =>
                monthColumns.map((column) => ({
                    key: `${unitId}_${column.key}`,
                    range: "custom",
                    start: column.start,
                    end: column.end,
                    business_unit_id: unitId,
                    level: "L2" as const,
                }))
            ),
        }),
        [allUnitIds, monthColumns]
    );

    const {
        data: chartBatchResult,
        isFetching: chartLoading,
        error: chartError,
    } = useGetBatchFinancialKpisDataQuery(chartBatchArgs, {
        skip: allUnitIds.length === 0,
    });

    // Fetch data for yearly columns (for expanded rows)
    const yearlyBatchArgs = useMemo(
        () => ({
            business_unit_ids: allUnitIds,
            ranges: yearlyColumns.map((column) => column.range),
            level: "L2",
            metrics: ["cashflow"],
        }),
        [yearlyColumns, allUnitIds]
    );

    const {
        data: yearlyBatchResult,
        isFetching: yearlyLoading,
        error: yearlyError,
    } = useGetBatchFinancialKpisDataQuery(yearlyBatchArgs, {
        skip: allUnitIds.length === 0,
    });

    const loading = chartLoading || yearlyLoading;
    const error = chartError || yearlyError;

    const rows = useMemo(() => {
        const selectedUnitIds = new Set(selectedUnits.map(u => String(u.value)));
        return comparisonUnits
            .filter(unit => selectedUnitIds.has(String(unit.value)))
            .map((unit) => {
                const unitId = String(unit.value);
                const monthlyValues = Object.fromEntries(
                    monthColumns.map((column) => {
                        const data = chartBatchResult?.results[`${unitId}_${column.key}`];
                        return [column.key, data?.cashflow ?? null];
                    })
                );
                const cashflowValues = Object.fromEntries(
                    yearlyColumns.map((column) => {
                        const data = yearlyBatchResult?.results[`${unitId}_${column.range}`];
                        return [column.key, data?.cashflow ?? null];
                    })
                );
                const cashflowMonthlyValues = Object.fromEntries(
                    yearlyColumns.map((column) => {
                        const data = yearlyBatchResult?.results[`${unitId}_${column.range}`];
                        return [column.key, data?.cashflow_monthly ?? null];
                    })
                );
                return { unitId, label: unit.label, monthlyValues, cashflowValues, cashflowMonthlyValues };
            });
    }, [chartBatchResult?.results, yearlyBatchResult?.results, comparisonUnits, selectedUnits, monthColumns, yearlyColumns]);

    // Calculate total row (sum across all selected units)
    const totalRow = useMemo(() => {
        const monthlyTotals = Object.fromEntries(
            monthColumns.map((column) => {
                const total = rows.reduce((sum, row) => sum + (row.monthlyValues[column.key] ?? 0), 0);
                return [column.key, total];
            })
        );
        const yearlyTotals = Object.fromEntries(
            yearlyColumns.map((column) => {
                const total = rows.reduce((sum, row) => sum + (row.cashflowValues[column.key] ?? 0), 0);
                return [column.key, total];
            })
        );
        return { monthlyTotals, yearlyTotals };
    }, [rows, monthColumns, yearlyColumns]);

    const handleSelectAll = () => {
        setSelectedUnits(defaultSelectedUnits);
    };

    const handleClear = () => {
        setSelectedUnits([]);
    };

    const toggleUnitExpansion = (unitId: string) => {
        setExpandedUnitIds((current) => {
            const next = new Set(current);
            if (next.has(unitId)) {
                next.delete(unitId);
            } else {
                next.add(unitId);
            }
            return next;
        });
    };

    return (
        <div className="space-y-5">
            <div>
                <h5 className="text-base font-semibold text-black dark:text-white">{title} Detail</h5>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    View cashflow across the last 12 months. Expand each unit to see yearly breakdown with monthly averages.
                </p>
            </div>

            <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div />
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleSelectAll}
                        className="text-xs text-primary hover:underline whitespace-nowrap"
                    >
                        Select all
                    </button>
                    <DropdownCheckbox
                        selected={selectedUnits}
                        onFilter={(selected) => setSelectedUnits(selected)}
                        data={comparisonUnits}
                        label="Units"
                    />
                </div>
            </div>

            {loading && <p className="text-sm text-gray-500">Loading cashflow data...</p>}
            {!loading && error && <p className="text-sm text-red-500">Unable to load cashflow data.</p>}
            {!loading && !error && selectedUnits.length === 0 && (
                <p className="text-sm text-gray-500">Select at least one unit to view cashflow.</p>
            )}

            {!loading && !error && selectedUnits.length > 0 && (
                <div className="space-y-5">
                    {/* Table Section */}
                    <div className="rounded-sm border border-stroke bg-gray-50 p-5 dark:border-strokedark dark:bg-meta-4">
                        <div className="space-y-5">
                            <div className="rounded-sm border border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
                                <p className="text-sm font-semibold text-black dark:text-white">
                                    Cashflow Breakdown
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                    Click on any unit row to view yearly breakdown. Top row shows total cashflow, second row shows monthly average.
                                </p>
                                <p className="text-xs mt-2 font-medium uppercase tracking-wide text-gray-500">
                                    Default view: Last 12 months | Expanded view: Yearly with monthly average
                                </p>
                            </div>

                            <div className="overflow-x-auto rounded-sm border border-stroke bg-white dark:border-strokedark dark:bg-boxdark">
                                <table className="w-full table-auto">
                                    <thead>
                                        <tr className="bg-gray-100 dark:bg-meta-4">
                                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                Unit
                                            </th>
                                            {monthColumns.map((column) => (
                                                <th key={column.key} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                    {column.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => (
                                            <CashflowUnitRow
                                                key={row.unitId}
                                                row={row}
                                                monthColumns={monthColumns}
                                                yearlyColumns={yearlyColumns}
                                                isExpanded={expandedUnitIds.has(row.unitId)}
                                                onToggle={() => toggleUnitExpansion(row.unitId)}
                                            />
                                        ))}
                                        {/* Total Row */}
                                        <tr className="border-t-2 border-stroke bg-gray-100 font-semibold dark:border-strokedark dark:bg-meta-4">
                                            <td className="px-4 py-3 text-sm text-black dark:text-white">
                                                TOTAL
                                            </td>
                                            {monthColumns.map((column) => (
                                                <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                                                    {formatValue(totalRow.monthlyTotals[column.key], "currency")}
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function CashflowUnitRow({
    row,
    monthColumns,
    yearlyColumns,
    isExpanded,
    onToggle,
}: {
    row: {
        unitId: string;
        label: string;
        monthlyValues: Record<string, number | null>;
        cashflowValues: Record<string, number | null>;
        cashflowMonthlyValues: Record<string, number | null>;
    };
    monthColumns: MonthlyChartColumn[];
    yearlyColumns: HistoricalColumn[];
    isExpanded: boolean;
    onToggle: () => void;
}) {
    return (
        <Fragment key={row.unitId}>
            {/* Main row: always show monthly values */}
            <tr
                key={row.unitId}
                className="cursor-pointer border-t border-stroke hover:bg-gray-50 dark:border-strokedark dark:hover:bg-meta-4/20"
                onClick={onToggle}
            >
                <td className="px-4 py-3 text-sm font-medium text-black dark:text-white">
                    <div className="flex items-center gap-3 text-left">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-stroke text-xs font-semibold text-bodydark2 dark:border-strokedark">
                            {isExpanded ? "-" : "+"}
                        </span>
                        <span>{row.label}</span>
                    </div>
                </td>
                {monthColumns.map((column) => (
                    <td key={column.key} className="px-4 py-3 text-sm text-black dark:text-white">
                        {formatValue(row.monthlyValues[column.key], "currency")}
                    </td>
                ))}
            </tr>

            {/* Expanded: show yearly breakdown as nested rows */}
            {isExpanded && (
                <tr key={`${row.unitId}:expanded`} className="bg-gray-50 dark:bg-meta-4/10">
                    <td colSpan={monthColumns.length + 1} className="px-4 py-4">
                        <div className="rounded border border-stroke bg-white p-4 dark:border-strokedark dark:bg-boxdark">
                            <p className="mb-3 text-xs font-semibold uppercase text-gray-500">
                                Yearly Breakdown
                            </p>
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-stroke dark:border-strokedark">
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                                Year
                                            </th>
                                            {yearlyColumns.map((column) => (
                                                <th key={column.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                                    {column.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="px-3 py-2 text-xs text-black dark:text-white">
                                                Total
                                            </td>
                                            {yearlyColumns.map((column) => (
                                                <td key={column.key} className="px-3 py-2 text-xs text-black dark:text-white">
                                                    {formatValue(row.cashflowValues[column.key], "currency")}
                                                </td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <td className="px-3 py-2 text-xs italic text-gray-600 dark:text-gray-400">
                                                Monthly Avg
                                            </td>
                                            {yearlyColumns.map((column) => (
                                                <td key={column.key} className="px-3 py-2 text-xs italic text-gray-600 dark:text-gray-400">
                                                    {formatMonthlyValue(row.cashflowMonthlyValues[column.key])}
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </Fragment>
    );
}
